import type { AutomationSettings } from "./types";

export type Hours = Pick<AutomationSettings, "activeStart" | "activeEnd" | "timezone" | "activeHoursMode" | "weeklyActiveHours">;
export const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
export function validWeek(v: unknown): v is boolean[][] {
  return Array.isArray(v) && v.length === 7 && v.every(d => Array.isArray(d) && d.length === 48 && d.every(x => typeof x === "boolean"));
}
export function dailyWeek(start: string, end: string): boolean[][] {
  const mins = (s: string) => { const [h, m] = s.split(":").map(Number); return h * 60 + m; };
  const a = mins(start), b = mins(end);
  return Array.from({ length: 7 }, () => Array.from({ length: 48 }, (_, i) => a === b || (a < b ? i * 30 >= a && i * 30 < b : i * 30 >= a || i * 30 < b)));
}
const formats = new Map<string, Intl.DateTimeFormat>();
export function weeklyActive(now: number, a: Hours): boolean {
  if (!validWeek(a.weeklyActiveHours)) return false;
  let f = formats.get(a.timezone);
  if (!f) {
    const options: Intl.DateTimeFormatOptions = { weekday: "long", hour: "numeric", minute: "numeric", hourCycle: "h23" };
    try { f = new Intl.DateTimeFormat("en-US", { ...options, timeZone: a.timezone }); }
    catch { f = new Intl.DateTimeFormat("en-US", options); }
    formats.set(a.timezone, f);
  }
  const p = f.formatToParts(now);
  const part = (key: string) => p.find(x => x.type === key)?.value ?? "";
  return a.weeklyActiveHours[WEEKDAYS.indexOf(part("weekday"))]?.[(Number(part("hour")) % 24) * 2 + Math.floor(Number(part("minute")) / 30)] ?? false;
}
/** Iterate real instants so skipped/repeated DST hours follow the calendar correctly. */
export function nextWeeklyStart(now: number, a: Hours): number {
  if (!validWeek(a.weeklyActiveHours) || !a.weeklyActiveHours.flat().some(Boolean)) return now + 7 * 86400_000;
  let t = now - now % 60_000 + 60_000;
  for (let i = 0; i < 8 * 1440; i++, t += 60_000) if (weeklyActive(t, a)) return t;
  return now + 7 * 86400_000;
}
export function activeHoursLabel(a: Hours): string {
  return a.activeHoursMode === "always" || (a.activeHoursMode !== "weekly" && a.activeStart === a.activeEnd) ? "24/7" : a.activeHoursMode === "weekly" ? "weekly calendar" : `${a.activeStart}–${a.activeEnd}`;
}
/** Older installations understand only one daily window; never run a weekly calendar incorrectly. */
export function hoursForExtension(a: AutomationSettings, version: string): AutomationSettings {
  const v = /^(\d+)\.(\d+)\.(\d+)$/.exec(version);
  const supported = v && (Number(v[1]) > 0 || Number(v[2]) > 1 || (Number(v[2]) === 1 && Number(v[3]) >= 10));
  if (supported) return a;
  if (a.activeHoursMode === "always") return { ...a, activeStart: "00:00", activeEnd: "00:00" };
  if (a.activeHoursMode === "weekly") return { ...a, enabled: false };
  return a;
}
/** Two independently placed blocks per day, generated once and saved like hand-edited slots. */
export function randomWeek(random = Math.random): boolean[][] {
  return Array.from({ length: 7 }, () => {
    const day = Array<boolean>(48).fill(false);
    for (const base of [12, 28]) {
      const start = base + Math.floor(random() * 5);
      const duration = 4 + Math.floor(random() * 5);
      for (let i = start; i < start + duration; i++) day[i] = true;
    }
    return day;
  });
}
